import { SKIP, visit } from 'unist-util-visit'
import type { Root, Text, Element, ElementContent } from 'hast'
import { kCitationMarkerRegex, kAgentLegalCitationLinkHrefRegex } from '@/util/agent-citations'

/**
 * Data attributes carried by the citation spans this plugin emits. Module-private on
 * purpose: consumers read them via readAgentCitationSpanProps, never by attribute name.
 */
const kAgentCitationIdAttr = 'data-agent-citation_id'
const kAgentCitationIdIndexAttr = 'data-agent-citation_id_index'
const kAgentCitationLegalLinkAttr = 'data-agent-citation_legal_link'

export type AgentCitationSpanProps = {
  citationId: string
  /** Occurrence index for this citation id, in document order — maps to citationHighlightIndex in the viewer. */
  citationIdIndex: number
  /** True for legal citation links (children = the authority text); false for reference markers (renders as a badge). */
  isLegalCitationLink: boolean
}

/**
 * Read the citation data off a <span>'s props, or null if the span isn't a citation.
 * The single reader for the span contract emitted by rehypeAgentCitations below.
 */
export function readAgentCitationSpanProps(props: Record<string, unknown>): AgentCitationSpanProps | null {
  const citationId = props[kAgentCitationIdAttr]
  const citationIdIndex = props[kAgentCitationIdIndexAttr]
  if (typeof citationId !== 'string' || citationIdIndex === undefined) return null

  return {
    citationId,
    citationIdIndex: Number(citationIdIndex),
    isLegalCitationLink: kAgentCitationLegalLinkAttr in props,
  }
}

/**
 * Converts both citation forms in the parsed markdown into <span> elements carrying the
 * attributes read by readAgentCitationSpanProps:
 *
 * 1. Reference marker — "[ref_xxxxxx]" in a text node → a badge span.
 * 2. Legal citation link — an <a> whose href is a bare citation id (remark's parse of
 *    "[authority](ref_xxxxxx)") → a span keeping the authority text as children.
 *
 * One occurrence counter spans both forms in document order — the index maps to
 * `citationHighlightIndex` in the viewer, so mixed-form occurrences must count in
 * reading order. The <Markdown /> `components` prop then swaps these spans for React
 * components (see EventTextMarkdown).
 */
export function rehypeAgentCitations() {
  return (tree: Root) => {
    // Track occurrence counts for each citation ID, shared across both forms — scoped
    // per run so a reused transformer starts every tree at zero.
    const citationCounts: Record<string, number> = {}

    const nextIndexForCitation = (citation_id: string): number => {
      if (!citationCounts[citation_id]) {
        citationCounts[citation_id] = 0
      }
      return citationCounts[citation_id]++
    }

    const buildCitationSpan = (citation_id: string, isLegalCitationLink: boolean, children: ElementContent[]): Element => ({
      type: 'element',
      tagName: 'span',
      properties: {
        className: ['agent-citation'],
        [kAgentCitationIdAttr]: citation_id,
        [kAgentCitationIdIndexAttr]: nextIndexForCitation(citation_id),
        ...(isLegalCitationLink ? { [kAgentCitationLegalLinkAttr]: 'true' } : {}),
      },
      children,
    })
    // A single pass visits elements and text in document order, keeping the shared
    // occurrence counter correct across mixed legal-citation-link/reference-marker content.
    visit(tree, (node, index, parent) => {
      if (parent === undefined || index === undefined) return

      // Legal citation link: <a href="ref_xxxxxx"> from a markdown link targeting a citation id
      if (node.type === 'element' && node.tagName === 'a') {
        const href = node.properties?.href
        if (typeof href === 'string' && kAgentLegalCitationLinkHrefRegex.test(href)) {
          parent.children[index] = buildCitationSpan(href, true, node.children)
          // Don't descend into the authority text (e.g. a stray marker inside it)
          return SKIP
        }
        return
      }

      // Marker form: "[ref_xxxxxx]" inside a text node
      if (node.type !== 'text') return
      const textNode = node as Text
      const matches = textNode.value.matchAll(kCitationMarkerRegex)

      const children: ElementContent[] = []
      let lastIndex = 0

      for (const match of matches) {
        const start = match.index
        if (start === undefined) continue
        const end = start + match[0].length

        // Extract the ref_xxxxxx portion without the brackets []
        const citation_id = match[0].slice(1, -1)

        // Push preceding text if any
        if (start > lastIndex) {
          children.push({
            type: 'text',
            value: textNode.value.slice(lastIndex, start),
          })
        }

        children.push(buildCitationSpan(citation_id, false, [{ type: 'text', value: citation_id }]))

        lastIndex = end
      }

      if (children.length === 0) return

      // Push remaining text if any
      if (lastIndex < textNode.value.length) {
        children.push({
          type: 'text',
          value: textNode.value.slice(lastIndex),
        })
      }

      // Replace the original text node and continue after the inserted nodes
      parent.children.splice(index, 1, ...children)
      return index + children.length
    })
  }
}
